import {Form, FormButton, FormField, FormSubmitHandler, Separator} from 'platform/components';
import {Box, HStack, VStack, Show} from 'platform/foundation';
import * as Yup from 'yup';

import {DefaultValues} from 'react-hook-form';

import {ConsentChannel, ConsentDuration, ConsentRecipient, ConsentSubject} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {useBranches} from '@dms/shared';

import {Nullish} from 'shared/web';

import {ConsentTypeFormType} from './components/ConsentTypeFormTemplate';
import {ConsentTypeFormTemplates} from './components/ConsentTypeFormTemplates';
import {ConsentTypeRecipientField} from './components/ConsentTypeRecipientField';

interface GdprConsentTypeFormProps {
  submitTitle: string;
  onSubmit: FormSubmitHandler<ConsentTypeFormType>;
  defaultValues?: DefaultValues<ConsentTypeFormType>;
  recipient?: ConsentRecipient | Nullish;
  cancelRoute: string;
}

export function GdprConsentTypeForm(props: GdprConsentTypeFormProps) {
  const {branchOptions} = useBranches();

  return (
    <Form<ConsentTypeFormType>
      onSubmit={props.onSubmit}
      schema={schema}
      defaultValues={props.defaultValues}
      shouldWatchForUnsavedChanges
    >
      {(control, formApi, navigateWithoutBlocker) => {
        const subject = formApi.watch('subject');
        const handleCancel = () => navigateWithoutBlocker(props.cancelRoute);

        return (
          <VStack>
            <Box width="50%">
              <FormField
                name="name"
                type="text"
                control={control}
                label={i18n.t('entity.gdpr.labels.consentType.name')}
                data-testid="consent-type-form-name"
                maxLength={100}
              />
            </Box>
            <Separator />
            <FormField
              name="subject"
              type="radio"
              control={control}
              label={i18n.t('entity.gdpr.labels.consentType.subject')}
              data-testid="consent-type-form-subject"
              options={subjectOptions}
            />
            <Show when={subject === ConsentSubject.BRANCH}>
              <>
                <Separator />
                <Box width="50%">
                  <FormField
                    name="branchId"
                    type="choice"
                    control={control}
                    label={i18n.t('entity.gdpr.labels.consentType.branch')}
                    data-testid="consent-type-form-branch"
                    options={branchOptions}
                  />
                </Box>
              </>
            </Show>
            <Separator />
            <ConsentTypeRecipientField
              control={control}
              recipient={props.recipient}
              label={
                subject === ConsentSubject.THIRD_PARTY
                  ? i18n.t('entity.gdpr.labels.consentType.thirdParty')
                  : i18n.t('entity.gdpr.labels.consentType.alsoValidForThirdParty')
              }
            />
            <Separator />
            <Box width="50%">
              <FormField
                name="duration"
                type="choice"
                control={control}
                label={i18n.t('entity.gdpr.labels.consentType.duration')}
                data-testid="consent-type-form-duration"
                options={durationOptions}
              />
            </Box>
            <Separator />
            <FormField
              name="channels"
              type="checkboxes"
              control={control}
              label={i18n.t('entity.gdpr.labels.consentType.channels')}
              data-testid="consent-type-form-channels"
              options={channelOptions}
            />
            <Separator />
            <VStack spacing={6}>
              <ConsentTypeFormTemplates control={control} />
              <HStack spacing={2} justify="flex-end">
                <FormButton
                  control={control}
                  variant="secondary"
                  onClick={handleCancel}
                  data-testid="consent-type-form-cancel"
                  title={i18n.t('general.actions.cancel')}
                />
                <FormButton
                  control={control}
                  type="submit"
                  variant="primary"
                  data-testid="consent-type-form-submit"
                  title={props.submitTitle}
                />
              </HStack>
            </VStack>
          </VStack>
        );
      }}
    </Form>
  );
}

const channelOptions = Object.values(ConsentChannel).map((channel) => ({
  value: channel,
  label: i18n.t(`general.labels.${channel}`),
}));

const durationOptions = Object.values(ConsentDuration).map((duration) => ({
  value: duration,
  label: i18n.t(`entity.gdpr.labels.consentType.durations.${duration}`),
}));

const subjectOptions = Object.values(ConsentSubject).map((subject) => ({
  value: subject,
  label: i18n.t(`entity.gdpr.labels.consentType.subjects.${subject}`),
}));

const schema = Yup.object().shape({
  name: Yup.string().required(i18n.t('general.validations.fieldIsRequired')),
  subject: Yup.string().required(i18n.t('general.validations.fieldIsRequired')),
  duration: Yup.string().required(i18n.t('general.validations.fieldIsRequired')),
  branchId: Yup.string()
    .nullable()
    .when('subject', {
      is: ConsentSubject.BRANCH,
      then: Yup.string().nullable().required(i18n.t('general.validations.fieldIsRequired')),
    }),
  recipientCustomerId: Yup.string()
    .nullable()
    .when('subject', {
      is: ConsentSubject.THIRD_PARTY,
      then: Yup.string().nullable().required(i18n.t('general.validations.fieldIsRequired')),
    }),
  channels: Yup.array()
    .min(1, i18n.t('entity.gdpr.validations.consentType.channelsAreRequired'))
    .required(i18n.t('entity.gdpr.validations.consentType.channelsAreRequired')),
});
